import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { SegmentEvent } from '@app/shared/interfaces';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { BehaviorSubject, combineLatest, interval, of, shareReplay } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { TranscriptionType, VoiceToTextService } from '../../../services/ai/voice-to-text.service';
import { CopilotService } from '../../../services/copilot/copilot.service';
import { PusherService } from '../../../services/pusher/pusher.service';
import { DialogDeleteGenericComponent } from '../../dialog-delete-generic/dialog-delete-generic.component';
import {
  ContentBlockType,
  ConversationStatus,
  CopilotConversation,
  CopilotMessage,
  CopilotTextContentBlock,
} from '../models/copilot.interface';

// Event interfaces for message interactions
export interface MessageFeedbackEvent {
  messageUuid: string;
  rating: 'liked' | 'disliked';
}

export interface MessageRetryEvent {
  messageUuid: string;
}

enum CopilotFeedbackRating {
  LIKE = 'liked',
  DISLIKE = 'disliked',
}

@Component({
  selector: 'sbnb-copilot',
  templateUrl: './copilot.component.html',
  styleUrls: ['./copilot.component.scss'],
})
export class CopilotComponent extends OnDestroyComponent implements OnInit, OnDestroy {
  @ViewChild('conversationContainer') conversationContainer: ElementRef;
  @ViewChild('questionTextarea') questionTextarea: ElementRef;
  @ViewChild('drawer') drawer: MatDrawer;

  // Dependencies
  private destroyRef = inject(DestroyRef);

  // Expose enums to template
  readonly ContentBlockType = ContentBlockType;

  // Track active conversation ID
  currentConversationUuid?: string;
  currentConversationUuid$ = new BehaviorSubject<string | undefined>(undefined);

  // Observable for messages in current conversation
  messages$ = this.currentConversationUuid$.pipe(
    switchMap((uuid) => {
      if (!uuid) return of([]);
      return this.copilotService.getMessagesForConversation(uuid);
    }),
    shareReplay(1)
  );

  // Track message states
  private messageFeedback = new Map<string, 'liked' | 'disliked'>();
  private messageErrors = new Map<string, boolean>();

  // Subscribe to error states from service
  private errorStateSubscription = this.currentConversationUuid$
    .pipe(
      switchMap((uuid) => {
        if (!uuid) return of(new Map<string, boolean>());
        return this.copilotService.messageState$.pipe(map((state) => state.errors));
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((errors) => {
      this.messageErrors = new Map(errors);
    });

  trackByMessageId(index: number, message: CopilotMessage): string {
    return message.uuid;
  }

  getMessageFeedback(messageUuid: string): 'liked' | 'disliked' | undefined {
    return this.messageFeedback.get(messageUuid);
  }

  hasMessageError(messageUuid: string): boolean {
    return this.messageErrors.get(messageUuid) || false;
  }

  // Voice-to-text session management as signal
  public voiceSessionId = signal<string | undefined>(undefined);

  // Mobile state management
  isMobile$ = this.deviceDetectionService.$isMobile.pipe(takeUntilDestroyed(this.destroyRef), shareReplay(1));

  // Direct computed signals from service - conditional on sessionId being defined
  voiceFinalText = computed(() => {
    const sessionId = this.voiceSessionId();
    return sessionId ? this.voiceToTextService.$finalText(sessionId) : '';
  });
  voiceWasRecorded = computed(() => {
    const sessionId = this.voiceSessionId();
    return sessionId ? this.voiceToTextService.wasRecorded(sessionId) : false;
  });
  voiceIsEditing = computed(() => {
    const sessionId = this.voiceSessionId();
    return sessionId ? this.voiceToTextService.isEditing(sessionId) : false;
  });

  // Single source of truth - all data comes from store
  conversations$ = this.copilotService.conversations$.pipe(
    map((conversationMap) => ({
      data: Object.values(conversationMap)
        .filter((entry) => entry.apiData)
        .map((entry) => entry.apiData)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    }))
  );

  // Single source of truth for progress message - reactive to conversation changes
  progressMessage$ = combineLatest([this.currentConversationUuid$, this.copilotService.conversations$]).pipe(
    map(([uuid, conversations]) => (uuid ? conversations[uuid]?.progressMessage || null : null)),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Streaming progress message that reveals characters gradually
  streamingProgressMessage$ = this.progressMessage$.pipe(
    distinctUntilChanged(),
    switchMap((message) => {
      if (!message) {
        return of('');
      }

      // Create an interval that emits every 30ms
      return interval(30).pipe(
        startWith(0),
        scan((acc) => acc + 1, 0),
        map((charIndex) => message.substring(0, charIndex)),
        takeWhile((partial) => partial.length <= message.length),
        distinctUntilChanged()
      );
    }),
    shareReplay(1)
  );

  // Form control and UI state
  questionControl = new FormControl('', [Validators.required, Validators.minLength(1)]);
  isLoading = false;
  questionSuggestions: string[] = [];
  showSuggestions = true;

  // Track if current input originated from a suggestion
  private currentInputFromSuggestion = false;

  CopilotFeedbackRating = CopilotFeedbackRating;

  isCurrentConversationSubmitting = false;

  currentConversation$ = combineLatest([this.copilotService.conversations$, this.currentConversationUuid$]).pipe(
    takeUntil(this.destroyed$),
    tap(([conversationMap, uuid]) => {
      const conversation = conversationMap?.[uuid];
      if (!uuid) {
        this.isCurrentConversationSubmitting = false;
      } else {
        this.isCurrentConversationSubmitting =
          conversation?.apiData?.status === ConversationStatus.InProgress || !!conversation?.progressMessage;
      }
    }),
    map(([conversationMap, uuid]) => conversationMap?.[uuid]?.apiData)
  );

  // Separate observable for the full conversation entry (including shouldScrollToBottom)
  currentConversationEntry$ = combineLatest([this.copilotService.conversations$, this.currentConversationUuid$]).pipe(
    takeUntil(this.destroyed$),
    map(([conversationMap, uuid]) => conversationMap?.[uuid])
  );

  constructor(
    private dialogRef: MatDialogRef<CopilotComponent>,
    public copilotService: CopilotService,
    private pusherService: PusherService,
    private dialog: MatDialog,
    private segmentIoService: SegmentIoService,
    public csService: InternalCsService,
    private voiceToTextService: VoiceToTextService,
    private deviceDetectionService: DeviceDetectionService,
    private posthogService: PosthogService
  ) {
    super();
    // Load initial conversations
    this.copilotService.loadConversationList().subscribe();
    this.loadQuestionSuggestions();

    // Voice-to-text updates via direct service access
    effect(() => {
      const finalText = this.voiceFinalText();
      const wasRecorded = this.voiceWasRecorded();
      const isEditing = this.voiceIsEditing();

      // Update during streaming (editing) or when recording is complete
      if ((wasRecorded || isEditing) && this.questionControl.value !== finalText) {
        this.questionControl.setValue(finalText);
      }

      // Also update when text is cleared (finalText becomes empty but form control still has old text)
      if (!wasRecorded && !isEditing && finalText === '' && this.questionControl.value !== '') {
        this.questionControl.setValue(finalText); // Clear the form control
      }
    });

    // Auto-submit when transcription completes
    effect(() => {
      const sessionId = this.voiceSessionId();
      if (!sessionId) return;

      const wasRecorded = this.voiceWasRecorded();
      const isEditing = this.voiceIsEditing();
      const finalText = this.voiceFinalText();

      // Check if transcription just completed successfully with text
      // wasRecorded is true when state is Success, and isEditing is false when not in editing state
      if (wasRecorded && !isEditing && finalText.trim().length > 0) {
        // Check if we haven't already submitted this text
        const currentQuestionValue = this.questionControl.value;
        if (currentQuestionValue === finalText && !this.isCurrentConversationSubmitting) {
          // console.log('🎤 Auto-submitting transcribed text:', finalText);
          // We can auto-submit later if we want to. Deciding not to for now.
          // this.onSubmit();
        }
      }
    });
  }

  ngOnInit(): void {
    // Create voice-to-text session for this component instance only if enabled
    this.voiceToTextService.enabled$.pipe(take(1)).subscribe((isEnabled) => {
      if (isEnabled) {
        const sessionId = this.voiceToTextService.createEmptySession(TranscriptionType.Copilot, null);
        this.voiceSessionId.set(sessionId);
      }
    });

    this.startNewChat();

    // Initialize the Pusher subscription in the service
    this.copilotService.initializePusherSubscription(this.pusherService);

    // Subscribe to scroll triggers from Pusher events
    this.currentConversationEntry$
      .pipe(
        map((conversationEntry) => conversationEntry?.shouldScrollToBottom),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.scrollToBottom();
        // Clear the scroll flag after scrolling
        if (this.currentConversationUuid) {
          this.copilotService.updateConversationInStore(this.currentConversationUuid, {
            shouldScrollToBottom: false,
          });
        }
      });
  }

  ngOnDestroy(): void {
    // Clean up voice-to-text session when component is destroyed
    if (this.voiceSessionId()) {
      this.voiceToTextService.destroySession(this.voiceSessionId());
    }
    super.ngOnDestroy();
  }

  // Helper method to update conversation UUID - observables are now reactive
  private setCurrentConversationUuid(uuid?: string) {
    this.currentConversationUuid = uuid;
    this.currentConversationUuid$.next(uuid);
  }

  // Drawer control methods
  toggleDrawer(): void {
    this.drawer.toggle();
  }

  closeDrawer(checkForMobile = false): void {
    if (checkForMobile) {
      this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.drawer.close();
        }
      });
    } else {
      this.drawer.close();
    }
  }

  onDrawerConversationSelected(conversation: CopilotConversation): void {
    this.openConversation(conversation);
    // Close drawer on mobile after selecting conversation
    this.closeDrawer(true);
  }

  onDrawerNewChatClicked(): void {
    this.startNewChat();
    // Close drawer on mobile after starting new chat
    this.closeDrawer(true);
  }

  startNewChat(): void {
    this.setCurrentConversationUuid(undefined);
    this.questionControl.reset();
    this.showSuggestions = true;
    this.currentInputFromSuggestion = false; // Clear input suggestion flag

    // Always focus on the textarea when starting a new chat
    setTimeout(() => {
      this.questionTextarea?.nativeElement?.focus();
    }, 100);
  }

  openConversation(conversation: CopilotConversation): void {
    this.isLoading = true;
    this.setCurrentConversationUuid(conversation.uuid);

    // Load conversation details - store will be updated automatically
    this.copilotService.loadConversationDetails(conversation.uuid).subscribe({
      error: () => {
        this.handleConversationError();
      },
      complete: () => {
        this.isLoading = false;
        this.scrollToBottom(false); // Instant when opening conversation
      },
    });
  }

  private handleConversationError(): void {
    this.setCurrentConversationUuid(undefined);
    this.isLoading = false;
  }

  close(): void {
    this.dialogRef.close();
  }

  onSubmit(confirmedActions?: boolean | null): void {
    if (this.questionControl.invalid || this.isCurrentConversationSubmitting) {
      return;
    }

    this.showSuggestions = false;
    const question = this.questionControl.value;
    if (!question) {
      return;
    }

    // Track input submitted event
    this.segmentIoService.track(SegmentEvent.CopilotInputSubmitted, {
      conversation_uuid: this.currentConversationUuid,
      input: question,
      is_new_conversation: !this.currentConversationUuid,
      from_suggestion: this.currentInputFromSuggestion,
    });

    // Track suggestion usage if this input came from a suggestion
    if (this.currentInputFromSuggestion) {
      this.segmentIoService.track(SegmentEvent.CopilotSuggestionUsed, {
        conversation_uuid: this.currentConversationUuid,
        is_new_conversation: !this.currentConversationUuid,
        input: question,
      });

      // Clear the suggestion flag after tracking
      this.currentInputFromSuggestion = false;
    } else {
      // Track custom input if it didn't come from a suggestion
      this.segmentIoService.track(SegmentEvent.CopilotCustomInputSubmitted, {
        input: question,
        conversation_uuid: this.currentConversationUuid,
        is_new_conversation: !this.currentConversationUuid,
      });
    }

    // Add current conversation to submitting set
    if (this.currentConversationUuid) {
      this.copilotService.markConversationAsSubmitting(this.currentConversationUuid);
    }

    this.copilotService.askQuestion(question, this.currentConversationUuid, confirmedActions).subscribe({
      next: (response) => {
        // Clear voice text after successful submission
        const sessionId = this.voiceSessionId();
        if (sessionId) {
          this.voiceToTextService.clearText(sessionId);
        }

        // Clear requires_confirmation after successfully sending the message
        if (this.currentConversationUuid) {
          this.copilotService.updateConversationRequiresConfirmation(this.currentConversationUuid, false);
        }

        // Set the current conversation UUID to the new or existing conversation
        this.setCurrentConversationUuid(response.data.uuid);
        this.scrollToBottom();
      },
      error: () => {
        // Don't clear voice text on error - user might want to retry
        if (this.currentConversationUuid) {
          this.copilotService.clearConversationSubmittingState(this.currentConversationUuid);
        }
        // Keep showSuggestions false since we have an active conversation with an error
      },
    });

    this.questionControl.reset();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit(null); // Normal message, not a confirmation
    }
  }

  loadQuestionSuggestions(): void {
    this.copilotService.getQuestionSuggestions().subscribe({
      next: (response) => {
        this.questionSuggestions = response.data;
      },
      error: () => {
        this.questionSuggestions = [];
      },
    });
  }

  selectSuggestion(suggestion: string): void {
    // Set flag to track that this input originated from a suggestion
    this.currentInputFromSuggestion = true;

    this.questionControl.setValue(suggestion);
    this.onSubmit();
  }

  deleteConversation(event: Event, conversation: CopilotConversation): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '400px',
      data: {
        name: `"${conversation.description}"`,
        action: '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.confirmDelete(event, conversation);
      }
    });
  }

  confirmDelete(event: Event, conversation: CopilotConversation | null): void {
    if (!conversation) {
      return;
    }

    this.copilotService.deleteConversation(conversation.uuid).subscribe({
      next: () => {
        // If the deleted conversation was the current one, reset the view
        if (this.currentConversationUuid === conversation.uuid) {
          this.startNewChat();
        }
      },
      error: (error) => {
        console.error('Failed to delete conversation:', error);
      },
    });
  }

  handleFeedbackClick(event: MessageFeedbackEvent): void {
    const { messageUuid, rating } = event;

    if (this.messageFeedback.has(messageUuid)) {
      return;
    }

    // Update local state
    this.messageFeedback.set(messageUuid, rating);
    this.copilotService.setMessageFeedback(messageUuid, rating);

    // Get the message for metadata
    this.messages$.pipe(take(1)).subscribe((messages) => {
      const message = messages.find((m) => m.uuid === messageUuid);
      if (!message) return;

      // Extract text content from data blocks for analytics
      const textBlocks =
        message.data
          ?.filter((block) => block.type === ContentBlockType.Text)
          .map((block) => (block as CopilotTextContentBlock).content) || [];
      const textContent = textBlocks.join('\n\n');

      const metadata = {
        conversation_uuid: this.currentConversationUuid,
        message_uuid: messageUuid,
        response: textContent,
        $el: undefined, // Remove event target reference
      };

      if (rating === 'liked') {
        this.segmentIoService.track(SegmentEvent.CopilotMessageLiked, metadata);
      } else {
        this.segmentIoService.track(SegmentEvent.CopilotMessageDisliked, metadata);

        // Trigger survey for dislike feedback after a short delay
        setTimeout(() => {
          if (this.messageFeedback.get(messageUuid) === 'disliked') {
            // Track to Segment for analytics
            this.segmentIoService.track(SegmentEvent.CopilotSurveyTriggered, metadata);

            // Capture to PostHog for survey triggering
            this.posthogService.capture(SegmentEvent.CopilotSurveyTriggered, metadata);
          }
        }, 500);
      }

      // Save feedback in the service
      if (this.currentConversationUuid) {
        this.copilotService.saveMessageFeedback(messageUuid, rating, this.currentConversationUuid).subscribe();
      }
    });
  }

  private scrollToBottom(smooth = true): void {
    setTimeout(() => {
      if (this.conversationContainer) {
        const element = this.conversationContainer.nativeElement;
        element.scrollTo({
          top: element.scrollHeight,
          behavior: smooth ? 'smooth' : 'instant',
        });
      }
    }, 0);
  }

  handleRetryClick(event: MessageRetryEvent): void {
    const messageUuid = event.messageUuid;

    if (!messageUuid || this.isCurrentConversationSubmitting || !this.currentConversationUuid) {
      return;
    }

    // Clear error state for this message
    this.messageErrors.delete(messageUuid);
    this.copilotService.setMessageError(messageUuid, false);

    // Add current conversation to submitting set
    this.copilotService.markConversationAsSubmitting(this.currentConversationUuid);

    this.copilotService.retryMessage(messageUuid, this.currentConversationUuid).subscribe({
      next: () => {
        this.scrollToBottom();
      },
      error: () => {
        // Set error state for this message
        this.messageErrors.set(messageUuid, true);
        this.copilotService.setMessageError(messageUuid, true);
        if (this.currentConversationUuid) {
          this.copilotService.clearConversationSubmittingState(this.currentConversationUuid);
        }
      },
    });
  }

  onTextareaFocus(): void {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile) {
        // Delay to allow keyboard to open
        setTimeout(() => {
          if (this.questionTextarea) {
            this.questionTextarea.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 300);
      }
    });
  }

  confirmAction(): void {
    // Clear requires_confirmation before sending the message
    if (this.currentConversationUuid) {
      this.copilotService.updateConversationRequiresConfirmation(this.currentConversationUuid, false);
    }
    // Send "Yes" as a user message with confirmed_actions = true
    this.questionControl.setValue('Yes');
    this.onSubmit(true);
  }

  cancelAction(): void {
    // Clear requires_confirmation before sending the message
    if (this.currentConversationUuid) {
      this.copilotService.updateConversationRequiresConfirmation(this.currentConversationUuid, false);
    }
    // Send "No" as a user message with confirmed_actions = false
    this.questionControl.setValue('No');
    this.onSubmit(false);
  }

  protected readonly TranscriptionType = TranscriptionType;
}
